import { Injectable } from '@angular/core';
import { UserProfile } from 'src/app/auth/profile/profile.service';
import { SettingsService } from 'src/app/core/settings/settings.service';
import { TierService } from 'src/app/shared/services/tier.service';
import { OrganizationService } from 'src/app/chatstyle/organization/organization.service';
import { Observable, of } from 'rxjs';
import { tap, catchError } from 'rxjs/operators';

import { menu } from 'src/app/auth/menu';
import { botMenu } from 'src/app/auth/botmenu';
import { customerMenu } from 'src/app/auth/customermenu';
import { MenuItem } from 'src/app/auth/menu-items';
import { Organization } from 'src/app/shared/models/core.model';

@Injectable({
  providedIn: 'root',
})
export class MenuService {
  menuItems: MenuItem[] = [];
  botMenuItems: MenuItem[] = [];
  customerMenuItems: MenuItem[] = [];

  profile: UserProfile;
  private currentTier: string | null = null;

  constructor(
    private settings: SettingsService,
    private tierService: TierService,
    private orgService: OrganizationService
  ) {}

  private loadCurrentTier(): Observable<void | Organization> {
    return this.orgService.getMyOrganization().pipe(
      tap(org => {
        this.currentTier = org?.tier || null;
      }),
      catchError(error => {
        console.error('Error loading organization tier:', error);
        this.currentTier = null;
        return of(null);
      }),
      tap(() => {})
    );
  }

  load(profile: UserProfile): Observable<void | Organization> {
    this.profile = profile;

    // Ensure tier is loaded before building menu
    return this.loadCurrentTier().pipe(
      tap(() => {
        this.menuItems = [];
        this.botMenuItems = [];
        this.customerMenuItems = [];

        this.addMenu(menu);
        this.addBotMenu(botMenu);
        this.addCustomerMenu(customerMenu);
      })
    );
  }

  /**
   * Refresh tier and reload menus (call after organization change)
   */
  refreshTier(): Observable<void | Organization> {
    if (this.profile) {
      return this.load(this.profile);
    }
    return of(undefined);
  }

  private addMenuItems(items: MenuItem[], targetArray: MenuItem[]) {
    items.forEach((item) => {
      if (this.hasPrivilegeAccess(item) && this.hasTierAccess(item)) {
        targetArray.push(item);
      }
    });
  }

  private hasTierAccess(item: MenuItem): boolean {
    // SuperAdmins have access to all pages regardless of tier
    if (this.hasPrivilege('SuperAdmin')) {
      return true;
    }

    // If item is not tier restricted, allow access
    if (!item.tierRestricted) {
      return true;
    }

    // Check if the menu item is visible for the current tier using the menu item text directly
    return this.tierService.isMenuItemVisible(this.currentTier, item.text);
  }

  addMenu(items: MenuItem[]) {
    this.addMenuItems(items, this.menuItems);
  }

  addBotMenu(items: MenuItem[]) {
    this.addMenuItems(items, this.botMenuItems);
  }

  addCustomerMenu(items: MenuItem[]) {
    this.addMenuItems(items, this.customerMenuItems);
  }

  getMenu() {
    return this.processMenuItems(this.menuItems);
  }

  hasPrivilege(priv: string): boolean {
    if (this.profile) {
      if (
        this.profile.privileges !== null &&
        this.profile.privileges.indexOf(priv) >= 0
      ) {
        return true;
      }
    }
    return false;
  }

  getBotMenu(botID: string, state: string, configurationType: string) {
    return this.processMenuItems(
      this.botMenuItems,
      botID,
      state,
      configurationType
    );
  }

  getCustomerMenu(botID: string, state: string, configurationType: string) {
    return this.processMenuItems(
      this.customerMenuItems,
      botID,
      state,
      configurationType
    );
  }

  private hasPrivilegeAccess(item: MenuItem): boolean {
    if (!item.privilege) {
      return true;
    }

    let priv = item.privilege;
    let negate = false;

    if (item.privilege.startsWith('!')) {
      priv = item.privilege.substr(1);
      negate = true;
    }

    let hasPriv = false;
    if (this.profile && this.profile.privileges) {
      hasPriv = this.profile.privileges.indexOf(priv) > -1;
    }

    return negate ? !hasPriv : hasPriv;
  }

  private processMenuItems(
    items: Array<any>,
    botID?: string,
    state?: string,
    configurationType?: string
  ): Array<any> {
    const clonedArray = [];
    const values = this.settings;
    const isSuperAdmin = this.hasPrivilege('SuperAdmin');

    items.forEach((value) => {
      const clone = Object.assign({}, value);

      if (value.link && botID) {
        clone.link = value.link.replace(':id', botID);
        if (configurationType) {
          clone.link = clone.link.replace(':type', configurationType);
        }
      }

      if (value.elink) {
        clone.elink = value.elink.replace(
          ':help_uri',
          values.getSetting('help_uri')
        );
      }

      // SuperAdmin bypass: Show all menu items regardless of state
      if (isSuperAdmin) {
        clonedArray.push(clone);
      } else {
        // Non-SuperAdmin: Apply state filtering
        if (!state || !clone.state || clone.state === state) {
          clonedArray.push(clone);
        }
      }
    });

    return clonedArray;
  }
}
